import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import './Adbusiness.css';
import { api, ENDPOINTS, SERVICE_BASE_URLS } from "../../services/apiConfig";
import axios from "axios";

// SVG Components with improved visuals
const BusinessInfoSVG = React.memo(() => (
  <svg width="100%" height="100%" viewBox="0 0 703 635" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M473.95 354.41H210.35V361.16H473.95V354.41Z" fill="#0d1b2a"/>
    <path d="M473.95 376.71H210.35V383.46H473.95V376.71Z" fill="#0d1b2a"/>
    <path d="M473.95 399.02H210.35V405.77H473.95V399.02Z" fill="#0d1b2a"/>
    <path d="M473.95 421.32H210.35V428.07H473.95V421.32Z" fill="#0d1b2a"/>
    <path d="M473.95 443.62H210.35V450.37H473.95V443.62Z" fill="#0d1b2a"/>
    <rect x="150" y="150" width="400" height="300" rx="10" stroke="#0d1b2a" strokeWidth="2" fill="none"/>
    <rect x="180" y="190" width="340" height="40" rx="5" fill="#0d1b2a" fillOpacity="0.1"/>
    <circle cx="200" cy="210" r="10" fill="#0d1b2a"/>
  </svg>
));

const ContactDetailsSVG = React.memo(() => (
  <svg width="100%" height="100%" viewBox="0 0 703 635" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="351.5" cy="317.5" r="200" stroke="#0d1b2a" strokeWidth="2" fill="none"/>
    <path d="M351.5 217.5C300.5 217.5 259.5 258.5 259.5 309.5C259.5 372.5 351.5 417.5 351.5 417.5C351.5 417.5 443.5 372.5 443.5 309.5C443.5 258.5 402.5 217.5 351.5 217.5ZM351.5 342.5C333.5 342.5 319.5 328.5 319.5 310.5C319.5 292.5 333.5 278.5 351.5 278.5C369.5 278.5 383.5 292.5 383.5 310.5C383.5 328.5 369.5 342.5 351.5 342.5Z" fill="#0d1b2a"/>
  </svg>
));

const DocumentUploadSVG = React.memo(() => (
  <svg width="100%" height="100%" viewBox="0 0 703 635" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M351.5 217.5V417.5" stroke="#0d1b2a" strokeWidth="2"/>
    <path d="M251.5 317.5L351.5 217.5L451.5 317.5" stroke="#0d1b2a" strokeWidth="2"/>
    <rect x="251.5" y="217.5" width="200" height="200" rx="10" stroke="#0d1b2a" strokeWidth="2" fill="none"/>
    <path d="M301.5 367.5H401.5" stroke="#0d1b2a" strokeWidth="2"/>
  </svg>
));

const FormField = ({ label, name, type, placeholder, value, onChange, error, options }) => (
  <div className="form-field">
    <label htmlFor={name}>{label}</label>
    {type === "select" ? (
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className={error ? "error" : ""}
        aria-invalid={error ? "true" : "false"}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    ) : (
      <input
        id={name}
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={error ? "error" : ""}
        aria-invalid={error ? "true" : "false"}
      />
    )}
    {error && <span className="error-message">{error}</span>}
  </div>
);

const FileUploadField = ({ label, name, onChange, error }) => (
  <div className="form-field file-field">
    <label htmlFor={name}>{label}</label>
    <div className="file-input-container">
      <input
        id={name}
        type="file"
        name={name}
        accept=".jpg,.jpeg,.png"
        onChange={onChange}
        className={error ? "error" : ""}
      />
      <span className="file-info">Formats: JPEG, PNG (Max: 5MB)</span>
    </div>
    {error && <span className="error-message">{error}</span>}
  </div>
);

const ProgressIndicator = ({ currentStep, totalSteps }) => (
  <div className="progress-indicator">
    {[...Array(totalSteps)].map((_, index) => (
      <div key={index} className="progress-step">
        <div className={`step-circle ${index + 1 <= currentStep ? "active" : ""}`}>
          {index + 1 <= currentStep ? "✓" : index + 1}
        </div>
        <div className="step-label">{getStepLabel(index + 1)}</div>
        {index < totalSteps - 1 && <div className={`step-connector ${index + 1 < currentStep ? "active" : ""}`} />}
      </div>
    ))}
  </div>
);

const getStepLabel = (step) => {
  switch (step) {
    case 1:
      return "Business Info";
    case 2:
      return "Contact Details";
    case 3:
      return "Documents";
    default:
      return `Step ${step}`;
  }
};

// Updated Regions and Districts of Ghana (2024)
const regions = [
  "Ahafo", "Ashanti", "Bono", "Bono East", "Central", "Eastern", "Greater Accra",
  "North East", "Northern", "Oti", "Savannah", "Upper East", "Upper West",
  "Volta", "Western", "Western North"
];

const districts = {
  "Ahafo": [
    "Asunafo North", "Asunafo South", "Asutifi North", "Asutifi South", 
    "Tano North", "Tano South"
  ],
  "Ashanti": [
    "Kumasi Metropolitan", "Asokwa", "Ejisu", "Obuasi Municipal", "Mampong", 
    "Bekwai", "Atwima Nwabiagya", "Ejura-Sekyedumase", "Offinso North", 
    "Offinso South", "Afigya-Kwabre", "Adansi North", "Adansi South"
  ],
  "Bono": [
    "Sunyani Municipal", "Berekum East", "Dormaa Central", "Wenchi", 
    "Tain", "Jaman South", "Jaman North", "Dormaa East", "Dormaa West"
  ],
  "Bono East": [
    "Techiman Municipal", "Nkoranza North", "Nkoranza South", 
    "Atebubu-Amantin", "Kintampo North", "Kintampo South", 
    "Pru East", "Pru West", "Sene East", "Sene West"
  ],
  "Central": [
    "Cape Coast Metropolitan", "Mfantseman", "Assin North", "Assin South", 
    "Twifo Atti-Morkwa", "Komenda-Edina-Eguafo-Abirem", "Agona West", 
    "Effutu", "Gomoa East", "Gomoa West", "Abura-Asebu-Kwamankese"
  ],
  "Eastern": [
    "New Juaben South", "Koforidua Municipal", "Akwapim North", 
    "Nsawam-Adoagyiri", "Kwahu East", "Atiwa East", "Atiwa West", 
    "Ayensuano", "Birim Central", "Suhum", "Upper Manya Krobo"
  ],
  "Greater Accra": [
    "Accra Metropolitan", "Tema Metropolitan", "Ashaiman Municipal", 
    "Adentan Municipal", "La Dade-Kotopon Municipal", "Ledzokuku Municipal", 
    "Krowor Municipal", "Ablekuma West", "Ablekuma Central", "Ablekuma North", 
    "Ayawaso East", "Ayawaso North", "Ayawaso West Wuogon", "Okaikwei North", 
    "Ga South", "Ga West", "Ga Central", "Ga East", "Ga North", 
    "Korle Klottey", "Weija-Gbawe", "Kpone-Katamanso", "Shai-Osudoku", 
    "Ningo-Prampram", "La-Nkwantanang-Madina", "Ayawaso Central", 
    "Sege", "Ada East", "Ada West"
  ],
  "North East": [
    "East Mamprusi", "West Mamprusi", "Bunkpurugu-Nyankpanduri", 
    "Chereponi", "Mamprugu Moagduri", "Yunyoo-Nasuan"
  ],
  "Northern": [
    "Tamale Metropolitan", "Sagnarigu", "Yendi Municipal", "Gushegu", 
    "Karaga", "Saboba", "Tatale-Sanguli", "Nanton", "Zabzugu", "Tolon"
  ],
  "Oti": [
    "Krachi East", "Krachi Nchumuru", "Nkwanta North", "Nkwanta South", 
    "Jasikan", "Biakoye", "Kadjebi", "Krachi West"
  ],
  "Savannah": [
    "Bole", "Central Gonja", "East Gonja", "North Gonja", 
    "West Gonja", "North East Gonja", "Sawla-Tuna-Kalba"
  ],
  "Upper East": [
    "Bolgatanga Municipal", "Bawku Municipal", "Kassena-Nankana West", 
    "Garu", "Binduri", "Talensi", "Nabdam", "Bawku West", "Pusiga"
  ],
  "Upper West": [
    "Wa Municipal", "Lambussie", "Lawra", "Nandom", 
    "Daffiama Bussie Issa", "Sissala East", "Sissala West", "Jirapa"
  ],
  "Volta": [
    "Ho Municipal", "Hohoe", "Keta", "Ketu South", "Ketu North", 
    "South Dayi", "North Dayi", "Adaklu", "Agotime-Ziope", "Akatsi North"
  ],
  "Western": [
    "Sekondi-Takoradi Metropolitan", "Ahanta West", "Jomoro", 
    "Nzema East", "Tarkwa-Nsuaem", "Ellembelle", "Shama", "Wassa East"
  ],
  "Western North": [
    "Bia West", "Bia East", "Bibiani-Anhwiaso-Bekwai", 
    "Juaboso", "Sefwi Wiawso", "Aowin", "Suaman"
  ]
};

const nationalities = ["Ghanaian", "Togolese"];

const Adbusiness = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    tin_number: "",
    business_location: "",
    region: "",
    business_phone_number: "",
    district: "",
    gh_post: "",
    landmark: "",
    nationality: "",
    personal_id: "",
    ghana_card_image: null,
    business_reg_cert_image: null,
    business_reg_number: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    const allowedTypes = ["image/jpeg", "image/png"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file) {
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid file type. Only JPEG and PNG allowed.");
        return;
      }

      if (file.size > maxSize) {
        toast.error("File too large. Max 5MB allowed.");
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: file }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const nextStep = () => {
    const stepErrors = validateStep();
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      const firstError = Object.values(stepErrors)[0];
      toast.error(firstError);
      return;
    }
    setStep((prev) => Math.min(prev + 1, 3));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateStep = useCallback(() => {
    const currentErrors = {};

    switch (step) {
      case 1:
        if (!formData.business_name) currentErrors.business_name = "Business name is required";
        if (!formData.tin_number) currentErrors.tin_number = "TIN number is required";
        if (!formData.business_location) currentErrors.business_location = "Business location is required";
        if (!formData.region) currentErrors.region = "Region is required";
        break;

      case 2:
        if (!formData.business_phone_number) currentErrors.business_phone_number = "Phone number is required";
        if (!formData.district) currentErrors.district = "District is required";
        if (!formData.nationality) currentErrors.nationality = "Nationality is required";
        break;

      case 3:
        // if (!formData.ghana_card_image) currentErrors.ghana_card_image = "Ghana Card image is required";
        // if (!formData.business_reg_cert_image) currentErrors.business_reg_cert_image = "Business registration certificate is required";
        if (!formData.business_reg_number) currentErrors.business_reg_number = "Business registration number is required";
        break;
    }

    return currentErrors;
  }, [step, formData]);


  const submitForm = async () => {
    const stepErrors = validateStep();
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      const firstError = Object.values(stepErrors)[0];
      toast.error(firstError);
      return;
    }
  
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem("token");
  
      if (!token) {
        toast.error("Authentication token not found. Please log in again.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
  
      // Create FormData exactly like in Postman
      const formPayload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formPayload.append(key, value);
        }
      });
      
      // Make a direct axios call to match Postman exactly
      const response = await axios({
        method: 'post',
        url: `${SERVICE_BASE_URLS.business}${ENDPOINTS.business.createbusiness}`,
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData, axios will set it correctly with boundary
        },
        data: formPayload
      });
  
      console.log("Business registration successful:", response.data);
  
      toast.success("Business details submitted successfully!", {
        position: "top-center",
        autoClose: 2000,
        onClose: () => navigate("/d/pricing-plans", { replace: true }),
      });
    } catch (error) {
      console.error("Error submitting business details:", error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         "Form submission failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="business-form-container">
      <ToastContainer position="top-center" autoClose={4000} hideProgressBar={false}
        newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      <div className="form-header">
        <h1>Business Registration</h1>
        <p>Complete your business profile to start selling on our platform</p>
      </div>

      <ProgressIndicator currentStep={step} totalSteps={3} />

      <div className="form-card">
        {step === 1 && (
          <div className="form-step-container">
            <div className="form-illustration">
              <BusinessInfoSVG />
              <div className="illustration-text">
                <h3>Business Information</h3>
                <p>Enter your basic business details to get started.</p>
              </div>
            </div>

            <div className="form-fields">
              <h2>Business Details</h2>
              <FormField
                label="Business Name"
                name="business_name"
                type="text"
                placeholder="Enter your business name"
                value={formData.business_name}
                onChange={handleChange}
                error={errors.business_name}
              />
              <FormField
                label="TIN Number"
                name="tin_number"
                type="text"
                placeholder="Enter your TIN number"
                value={formData.tin_number}
                onChange={handleChange}
                error={errors.tin_number}
              />
              <FormField
                label="Business Location"
                name="business_location"
                type="text"
                placeholder="Enter your business location"
                value={formData.business_location}
                onChange={handleChange}
                error={errors.business_location}
              />
              <FormField
                label="Region"
                name="region"
                type="select"
                placeholder="Select Region"
                value={formData.region}
                onChange={handleChange}
                error={errors.region}
                options={regions}
              />
              <div className="form-actions">
                <button className="btn-next" onClick={nextStep}>Continue to Contact Details</button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="form-step-container">
            <div className="form-illustration">
              <ContactDetailsSVG />
              <div className="illustration-text">
                <h3>Contact Details</h3>
                <p>Provide your business contact information for customers to reach you.</p>
              </div>
            </div>

            <div className="form-fields">
              <h2>Contact Information</h2>
              <FormField
                label="Business Phone Number"
                name="business_phone_number"
                type="tel"
                placeholder="Enter business phone number"
                value={formData.business_phone_number}
                onChange={handleChange}
                error={errors.business_phone_number}
              />
              <FormField
                label="District"
                name="district"
                type="select"
                placeholder="Select District"
                value={formData.district}
                onChange={handleChange}
                error={errors.district}
                options={formData.region ? districts[formData.region] : []}
              />
              <FormField
                label="GH Post Address (Optional)"
                name="gh_post"
                type="text"
                placeholder="Enter your GH Post address"
                value={formData.gh_post}
                onChange={handleChange}
              />
              <FormField
                label="Landmark (Optional)"
                name="landmark"
                type="text"
                placeholder="Enter a nearby landmark"
                value={formData.landmark}
                onChange={handleChange}
              />
              <FormField
                label="Nationality"
                name="nationality"
                type="select"
                placeholder="Select Nationality"
                value={formData.nationality}
                onChange={handleChange}
                error={errors.nationality}
                options={nationalities}
              />
              <FormField
                label="Personal ID (Optional)"
                name="personal_id"
                type="text"
                placeholder="Enter your personal ID"
                value={formData.personal_id}
                onChange={handleChange}
              />
              <div className="form-actions">
                <button className="btn-back" onClick={prevStep}>Back to Business Details</button>
                <button className="btn-next" onClick={nextStep}>Continue to Documents</button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="form-step-container">
            <div className="form-illustration">
              <DocumentUploadSVG />
              <div className="illustration-text">
                <h3>Document Upload</h3>
                <p>Upload the required documentation to verify your business.</p>
              </div>
            </div>

            <div className="form-fields">
              <h2>Document Verification</h2>
              <FileUploadField
                label="Ghana Card Image"
                name="ghana_card_image"
                onChange={handleFileChange}
                // error={errors.ghana_card_image}
              />
              <FileUploadField
                label="Business Registration Certificate"
                name="business_reg_cert_image"
                onChange={handleFileChange}
                // error={errors.business_reg_cert_image}
              />
              <FormField
                label="Business Registration Number"
                name="business_reg_number"
                type="text"
                placeholder="Enter your business registration number"
                value={formData.business_reg_number}
                onChange={handleChange}
                error={errors.business_reg_number}
              />
              <div className="form-actions">
                <button className="btn-back" onClick={prevStep}>Back to Contact Details</button>
                <button 
                  className="btn-submit" 
                  onClick={submitForm} 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Complete Registration"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Adbusiness;